import type { RoleplaySession, RoleplayTurn } from 'services/api/roleplayClient';
import type { ResponsePayload, VoiceFlowContentItem, VoiceFlowItemState } from '../../types';

export type RoleplayConversationMessage = {
  id: string;
  role: 'actor' | 'user';
  speaker: string;
  text: string;
  timestamp: string;
  order: number;
  isLive?: boolean;
  isStreaming?: boolean;
};

export type RoleplayLiveCaption = { text: string; timestamp: string; id: string };

const normalizedText = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase();
const timestampOrder = (timestamp: string, fallback: number) => {
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const actorGeneration = (value: unknown): number | null => (
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null
);
const actorMessageId = (sessionId: number, generation: number) => (
  `roleplay-${sessionId}-actor-generation-${generation}`
);

/** Project only public spoken dialogue; never retain evaluator/session metadata. */
export const buildRoleplayConversationMessages = ({
  sessionId,
  personaName,
  turns,
  learnerCaption,
  actorCaption,
  actorCaptionGeneration,
}: {
  sessionId: number;
  personaName: string;
  turns: RoleplayTurn[];
  learnerCaption?: RoleplayLiveCaption | null;
  actorCaption?: RoleplayLiveCaption | null;
  actorCaptionGeneration?: number | null;
}): RoleplayConversationMessage[] => {
  const publicTurns = turns.filter((turn) => turn.text.trim() && !turn.metadata?.safety_only)
    .sort((left, right) => left.sequence - right.sequence || left.id - right.id);
  const messages: RoleplayConversationMessage[] = publicTurns.map((turn) => {
    const generation = turn.speaker === 'persona' ? actorGeneration(turn.metadata?.generation) : null;
    return {
      id: generation !== null ? actorMessageId(sessionId, generation) : `roleplay-${sessionId}-turn-${turn.id}`,
      role: turn.speaker === 'persona' ? 'actor' : 'user',
      speaker: turn.speaker === 'persona' ? personaName : 'You',
      text: turn.text,
      timestamp: turn.created_at,
      order: timestampOrder(turn.created_at, turn.sequence),
    };
  });

  (['learner', 'persona'] as const).forEach((speaker) => {
    const caption = speaker === 'learner' ? learnerCaption : actorCaption;
    if (!caption?.text.trim()) return;
    const generation = speaker === 'persona' ? actorGeneration(actorCaptionGeneration) : null;
    // The provider may paraphrase a fixed directive while persistence retains
    // its canonical text. They are still one actor turn. Reconcile by the
    // owned session/generation, never by similar words or a clock threshold.
    const matchingActorTurn = generation !== null && publicTurns.some((turn) => (
      turn.speaker === 'persona' && actorGeneration(turn.metadata?.generation) === generation
    ));
    if (matchingActorTurn) return;
    const latestTurn = [...publicTurns].reverse().find((turn) => turn.speaker === speaker);
    const captionText = normalizedText(caption.text);
    const savedText = normalizedText(latestTurn?.text ?? '');
    // Live captions can remain while their just-persisted row arrives. Compare
    // only that speaker's current tail, with an occurrence-time boundary so a
    // separate later repetition remains a separate utterance.
    const isPersistedCaption = generation === null && latestTurn
      && timestampOrder(latestTurn.created_at, 0) >= timestampOrder(caption.timestamp, Infinity) - 1000
      && (savedText === captionText || savedText.startsWith(`${captionText} `));
    if (isPersistedCaption) return;
    messages.push({
      id: generation !== null ? actorMessageId(sessionId, generation)
        : `roleplay-${sessionId}-live-${speaker}-${caption.id}`,
      role: speaker === 'persona' ? 'actor' : 'user',
      speaker: speaker === 'persona' ? personaName : 'You',
      text: caption.text,
      timestamp: caption.timestamp,
      order: timestampOrder(caption.timestamp, Number.MAX_SAFE_INTEGER),
      isLive: true,
      isStreaming: speaker === 'persona',
    });
  });
  return messages.sort((left, right) => left.order - right.order);
};

/** Keep server-owned rows after the active host moves on; captions stay live only. */
export const mergeRoleplayConversationMessages = (
  previous: RoleplayConversationMessage[],
  current: RoleplayConversationMessage[],
): RoleplayConversationMessage[] => {
  const messages = new Map(previous.filter((message) => !message.isLive).map((message) => [message.id, message]));
  current.forEach((message) => messages.set(message.id, message));
  return [...messages.values()].sort((left, right) => left.order - right.order);
};

export type RoleplayConversationHistoryTarget = {
  questionId: number;
  canonicalSessionId: number | null;
  canonicalResponseId: number | null;
};

const positiveId = (value: unknown): number | null => (
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : null
);
const objectRecord = (value: unknown): Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : {}
);

/** Only selected completed activities and the current practice may load history. */
export const getRoleplayConversationHistoryTargets = ({
  contentSchema, itemStates, responses, flowRunId, currentQuestionIds,
}: {
  contentSchema: VoiceFlowContentItem[];
  itemStates: VoiceFlowItemState[];
  responses: Array<ResponsePayload & { id?: number }>;
  flowRunId: number;
  currentQuestionIds: Array<number | null | undefined>;
}): RoleplayConversationHistoryTarget[] => {
  const states = new Map(itemStates.map((state) => [state.flow_item_id, state]));
  const targets = new Map<number, RoleplayConversationHistoryTarget>();
  contentSchema.forEach((item) => {
    if (item.question_type !== 'roleplay' || positiveId(item.question_id) === null) return;
    const state = states.get(item.flow_item_id);
    if (state && state.question_id !== item.question_id) return;
    const completed = (state?.status ?? item.status) === 'complete';
    if (!completed && !currentQuestionIds.includes(item.question_id)) return;
    const response = completed ? [...responses].reverse().find((row) => (
      row.question === item.question_id && row.is_completed === true
      && (row.flow_run === undefined || row.flow_run === flowRunId)
    )) : undefined;
    const metadata = {
      ...objectRecord(objectRecord(item.structured_payload).response_metadata),
      ...objectRecord(objectRecord(state?.structured_payload).response_metadata),
    };
    const canonicalSession = completed ? metadata.roleplay_session_id ?? response?.metadata?.roleplay_session_id : null;
    const canonicalResponse = completed ? response?.id ?? metadata.user_response_id : null;
    if ((canonicalSession != null && positiveId(canonicalSession) === null)
      || (canonicalResponse != null && positiveId(canonicalResponse) === null)) return;
    targets.set(item.question_id, { questionId: item.question_id,
      canonicalSessionId: positiveId(canonicalSession), canonicalResponseId: positiveId(canonicalResponse) });
  });
  return [...targets.values()];
};

/** Restore real attempts without promoting a different attempt to the canonical win. */
export const buildRoleplayConversationHistory = (
  sessions: RoleplaySession[], flowRunId: number, target: RoleplayConversationHistoryTarget,
): RoleplayConversationMessage[] => {
  const publicSessions = sessions.slice(-100).filter((session) => (
    positiveId(session?.id) !== null && session.flow_run_id === flowRunId
    && session.scenario?.question_id === target.questionId
    && typeof session.scenario?.persona?.name === 'string' && Array.isArray(session.turns)
  ));
  if (target.canonicalSessionId !== null || target.canonicalResponseId !== null) {
    const canonicalWin = publicSessions.some((session) => (
      session.status === 'completed' && session.completion_reason === 'win_condition_met'
      && (target.canonicalSessionId === null || session.id === target.canonicalSessionId)
      && (target.canonicalResponseId === null || session.user_response_id === target.canonicalResponseId)
    ));
    if (!canonicalWin) return [];
  }
  return publicSessions.reduce<RoleplayConversationMessage[]>((messages, session) => (
    mergeRoleplayConversationMessages(messages, buildRoleplayConversationMessages({
      sessionId: session.id, personaName: session.scenario.persona.name, turns: session.turns,
    }))
  ), []);
};
